import { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const UseTimeChart = ({ changePage }) => {
  const [useTimes, setUseTimes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUseTimes = async () => {
      try {
        setLoading(true);
        const response = await fetch('/getUseTimes');
        const data = await response.json();
        setUseTimes(data);
      } catch (error) {
        console.error('Failed to fetch use times:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUseTimes();
  }, []);

  // 시간 포맷팅 함수
  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hours}시간 ${mins}분`;
  };

  // 통계 계산
  const totalTime = useTimes.reduce((sum, item) => sum + item.total, 0);
  const averageTime = useTimes.length > 0 ? totalTime / useTimes.length : 0;
  const topUser = useTimes.length > 0 ? useTimes.reduce((prev, current) => 
    (prev.total > current.total) ? prev : current
  ) : null;

  // 순위별로 정렬
  const sortedUseTimes = [...useTimes].sort((a, b) => b.total - a.total);

  // 차트 데이터
  const chartData = {
    labels: sortedUseTimes.map((item) => item._id),
    datasets: [
      {
        label: '프로그램 사용시간(분)',
        data: sortedUseTimes.map((item) => item.total),
        backgroundColor: [
          'rgba(99, 102, 241, 0.8)',
          'rgba(129, 140, 248, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(6, 182, 212, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(168, 85, 247, 0.8)',
          'rgba(236, 72, 153, 0.8)',
        ],
        borderColor: [
          'rgb(99, 102, 241)',
          'rgb(129, 140, 248)',
          'rgb(16, 185, 129)',
          'rgb(6, 182, 212)',
          'rgb(245, 158, 11)',
          'rgb(239, 68, 68)',
          'rgb(168, 85, 247)',
          'rgb(236, 72, 153)',
        ],
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        color: 'white',
        font: {
          weight: 'bold',
          size: 12,
        },
        formatter: (value) => (value / 60).toFixed(1) + 'h',
      },
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#64748b',
          font: {
            weight: 500,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(100, 116, 139, 0.1)',
        },
        ticks: {
          color: '#64748b',
          font: {
            weight: 500,
          },
          callback: function(value) {
            return (value / 60).toFixed(0) + 'h';
          },
        },
      },
    },
  };

  // 순위 아이콘 함수
  const getRankIcon = (index) => {
    switch(index) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return `${index + 1}위`;
    }
  };

  // 순위 색상 함수
  const getRankColor = (index) => {
    switch(index) {
      case 0: return 'text-warning';
      case 1: return 'text-muted';
      case 2: return 'text-info';
      default: return 'text-primary';
    }
  };

  if (loading) {
    return (
      <div className="modern-overlay">
        <div className="spinner-container">
          <div className="spinner-border modern-spinner" role="status"></div>
          <p className="loading-text">데이터 로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-dashboard use-time-page">
      <section id="container" className="modern-container">
        <div className="wrap-container">
          <div className="admin-header">
            <div className="admin-title-section">
              <h1 className="admin-title">
                <i className="fas fa-trophy me-3"></i>
                PC 사용시간 순위
              </h1>
              <p className="admin-subtitle">프로그램 사용시간 통계 및 순위 현황</p>
            </div>
            <button 
              className="back-to-main-btn"
              onClick={() => changePage('main')}
            >
              <i className="fas fa-home me-2"></i>
              메인으로 돌아가기
            </button>
          </div>
          
          <div className="modern-grid">
            {/* 차트 카드 */}
            <div className="modern-card device-card">
              <div className="card-header">
                <h4 className="card-title">
                  <i className="fas fa-chart-column me-2"></i>
                  사용시간 차트
                </h4>
              </div>
              <div className="card-body">
                <div className="chart-container" style={{ height: '250px' }}>
                  {useTimes.length > 0 ? (
                    <Bar data={chartData} options={chartOptions} />
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-chart-bar fa-3x text-muted mb-3"></i>
                      <p className="text-muted">표시할 데이터가 없습니다.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 순위 테이블 카드 */}
            <div className="modern-card daily-card">
              <div className="card-header">
                <h4 className="card-title">
                  <i className="fas fa-ranking-star me-2"></i>
                  상세 순위표
                </h4>
              </div>
              <div className="card-body">
                <div className="device-table-container">
                  {sortedUseTimes.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table modern-table">
                        <thead className="thead-dark">
                          <tr>
                            <th>
                              <i className="fas fa-medal me-1"></i>
                              순위
                            </th>
                            <th>
                              <i className="fas fa-user me-1"></i>
                              사용자
                            </th>
                            <th>
                              <i className="fas fa-clock me-1"></i>
                              사용시간
                            </th>
                            <th>
                              <i className="fas fa-percentage me-1"></i>
                              비율
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedUseTimes.map((item, index) => {
                            const topUserTime = sortedUseTimes[0]?.total || 1; // 1등의 사용시간
                            const percentage = ((item.total / topUserTime) * 100).toFixed(1);
                            return (
                              <tr key={item._id} className="modern-row">
                                <td className="text-center">
                                  <span className={`fw-bold ${getRankColor(index)}`}>
                                    {getRankIcon(index)}
                                  </span>
                                </td>
                                <td className="fw-bold">{item._id}</td>
                                <td>
                                  <span className="text-primary fw-bold">
                                    {formatTime(item.total)}
                                  </span>
                                  <br />
                                  <small className="text-muted">
                                    ({(item.total / 60).toFixed(1)}시간)
                                  </small>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="flex-grow-1 me-2">
                                      <div className="progress" style={{ height: '8px' }}>
                                        <div 
                                          className="progress-bar bg-primary" 
                                          role="progressbar" 
                                          style={{ width: `${percentage}%` }}
                                          aria-valuenow={percentage} 
                                          aria-valuemin="0" 
                                          aria-valuemax="100"
                                        ></div>
                                      </div>
                                    </div>
                                    <small className="text-success fw-bold">
                                      {percentage}%
                                    </small>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-users fa-3x text-muted mb-3"></i>
                      <p className="text-muted">순위 데이터가 없습니다.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UseTimeChart;
